<template>
  <div class="auth-page auth-page--login">
    <header class="auth-card__header auth-card__header--login">
      <h1 class="auth-card__title auth-card__title--login">Connexion</h1>
    </header>
    <div class="auth-page__wrap">
      <Transition name="card" appear>
        <div class="auth-card auth-card--login">
          <div
            v-if="sessionNotice"
            class="auth-form__notice"
            role="status"
            aria-live="polite"
          >
            <InformationCircleIcon class="auth-form__notice-icon" aria-hidden="true" />
            <span>{{ sessionNotice }}</span>
          </div>
          <form class="auth-form" @submit.prevent="handleSubmit">
            <div class="auth-form__group">
              <label for="username" class="auth-form__label">Nom d'utilisateur</label>
              <div class="auth-form__input-wrap">
                <UserCircleIcon class="auth-form__icon" />
                <input
                  id="username"
                  v-model="username"
                  type="text"
                  class="auth-form__input"
                  required
                  placeholder="Votre identifiant"
                  autocomplete="username"
                  :disabled="mfaStep"
                />
              </div>
            </div>

            <div class="auth-form__group">
              <label for="password" class="auth-form__label">Mot de passe</label>
              <div class="auth-form__input-wrap">
                <LockClosedIcon class="auth-form__icon" />
                <input
                  id="password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  class="auth-form__input auth-form__input--with-btn"
                  required
                  placeholder="••••••••"
                  autocomplete="current-password"
                  :disabled="mfaStep"
                />
                <button
                  type="button"
                  class="auth-form__toggle-pwd"
                  :aria-label="showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'"
                  :disabled="mfaStep"
                  @click="showPassword = !showPassword"
                >
                  <EyeIcon v-if="!showPassword" class="w-5 h-5" />
                  <EyeSlashIcon v-else class="w-5 h-5" />
                </button>
              </div>
            </div>

            <div v-if="!mfaStep" class="auth-form__options">
              <label class="auth-form__remember">
                <input v-model="rememberMe" type="checkbox" class="auth-form__checkbox" />
                <span class="auth-form__checkmark" />
                Se souvenir de moi
              </label>
              <router-link to="/mot-de-passe-oublié" class="auth-form__link">
                Mot de passe oublié ?
              </router-link>
            </div>

            <div v-if="errorMessage" class="auth-form__error">
              <ExclamationCircleIcon class="w-5 h-5 shrink-0" />
              <span>{{ errorMessage }}</span>
            </div>

            <template v-if="mfaStep">
              <p class="auth-form__notice">
                Un code de vérification a été envoyé à votre adresse email.
              </p>
              <div class="auth-form__group">
                <label for="mfaCode" class="auth-form__label">Code de vérification</label>
                <input
                  id="mfaCode"
                  v-model="mfaCode"
                  type="text"
                  class="auth-form__input"
                  maxlength="6"
                  required
                  placeholder="Code à 6 chiffres"
                  autocomplete="one-time-code"
                />
              </div>
              <button type="button" class="auth-form__submit" :disabled="mfaLoading" @click="submitMfaCode">
                <span v-if="!mfaLoading" class="auth-form__submit-text">Valider le code</span>
                <span v-else class="auth-form__spinner" aria-hidden="true" />
              </button>
              <button type="button" class="auth-form__link auth-form__group--full" @click="cancelMfaStep">
                Retour
              </button>
            </template>
            <template v-else>
            <button type="submit" class="auth-form__submit" :disabled="loading">
              <span v-if="!loading" class="auth-form__submit-text">Se connecter</span>
              <span v-else class="auth-form__spinner" aria-hidden="true" />
            </button>

            <p class="auth-form__footer">
              Nouveau client ?
              <router-link to="/inscrire" class="auth-form__link">Créer un compte</router-link>
            </p>
            </template>
          </form>
        </div>
      </Transition>
    </div>

   <Teleport to="body">
     <Transition name="modal">
       <div v-if="showEmailVerificationModal" class="auth-modal-overlay" @click.self="closeEmailVerificationModal">
         <div class="auth-modal-card">
           <div class="auth-modal-icon">
             <EnvelopeIcon class="w-16 h-16 text-amber-500" />
           </div>
           <p class="auth-modal-brand">patrimonea by Gwem</p>
           <h2 class="auth-modal-title">Vérifiez votre adresse email</h2>
           <p class="auth-modal-text">
             Votre compte a été créé mais votre adresse email n'est pas encore vérifiée.
             Un code vous a été envoyé par email. Saisissez-le ci-dessous pour activer votre compte.
           </p>

           <form class="auth-form" @submit.prevent="submitEmailVerification">
             <div class="auth-form__group auth-form__group--full">
               <label for="emailLoginVerify" class="auth-form__label">Email</label>
               <div class="auth-form__input-wrap">
                 <input
                   id="emailLoginVerify"
                   v-model="emailForVerification"
                   type="email"
                   class="auth-form__input"
                   required
                   placeholder="vous@exemple.com"
                 />
               </div>
             </div>

             <div class="auth-form__group auth-form__group--full">
               <label for="codeLoginVerify" class="auth-form__label">Code de vérification</label>
               <div class="auth-form__input-wrap">
                 <input
                   id="codeLoginVerify"
                   v-model="verificationCode"
                   type="text"
                   class="auth-form__input"
                   required
                   maxlength="6"
                   placeholder="Code à 6 chiffres"
                 />
               </div>
             </div>

             <div v-if="verificationError" class="auth-form__error auth-form__group--full">
               <ExclamationCircleIcon class="w-5 h-5 shrink-0" />
               <span>{{ verificationError }}</span>
             </div>

             <div class="auth-form__submit-wrap auth-form__group--full">
               <button type="submit" class="auth-form__submit" :disabled="verificationLoading">
                 <span v-if="!verificationLoading" class="auth-form__submit-text">Valider le code</span>
                 <span v-else class="auth-form__spinner" aria-hidden="true" />
               </button>
             </div>

            <p class="auth-form__footer auth-form__group--full">
              Vous n'avez pas reçu de code ?
              <button
                type="button"
                class="auth-form__link"
                :disabled="resendLoading"
                @click="resendVerificationCode"
              >
                Renvoyer le code
              </button>
            </p>
            <p
              v-if="resendSuccessMessage"
              class="auth-form__notice auth-form__group--full"
              role="status"
            >
              {{ resendSuccessMessage }}
            </p>
           </form>
         </div>
       </div>
     </Transition>
   </Teleport>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import {
  UserCircleIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationCircleIcon,
  EnvelopeIcon,
  InformationCircleIcon,
} from '@heroicons/vue/24/outline';
import axios from 'axios';
import { getErrorMessage, formatValidationErrors } from '@/utils/errorMessages';
import {
  storeUserProfileFromLogin,
  isAuthenticatedSession,
  verifyServerSession,
  clearAuthenticatedSession,
} from '@/utils/sessionAuth';
import { isSafeInternalPath } from '@/utils/safeUrl';
import { createSubmitGuard, formatThrottleMessage } from '@/utils/submitGuard';
import { trackLogin } from '@/utils/analytics';
import { resolveDefaultPostLoginPath } from '@/utils/postLoginRedirect';

const loginSubmitGuard = createSubmitGuard(2500);
const mfaSubmitGuard = createSubmitGuard(2000);
const loginVerifySubmitGuard = createSubmitGuard(2000);
const loginResendSubmitGuard = createSubmitGuard(10000);

const REMEMBER_USERNAME_KEY = 'pat_remember_username';

const router = useRouter();
const route = useRoute();

/** Cible interne après login (query `redirect`), sans open-redirect. */
function getSafeRedirectTarget() {
  let raw = route.query.redirect;
  if (Array.isArray(raw)) raw = raw[0];
  if (typeof raw !== 'string' || !raw.trim()) return null;
  let decoded;
  try {
    decoded = decodeURIComponent(raw.trim());
  } catch {
    return null;
  }
  if (!isSafeInternalPath(decoded)) return null;
  if (decoded === '/connexion' || decoded.startsWith('/connexion?')) return null;
  // Accueil n'a de sens que pour ceux qui ont les deux espaces.
  if (decoded === '/acceuil' || decoded.startsWith('/acceuil?')) {
    return resolveDefaultPostLoginPath();
  }
  return decoded;
}

async function navigateAfterLogin() {
  trackLogin('username');
  const target = getSafeRedirectTarget() || resolveDefaultPostLoginPath();
  try {
    await router.replace(target);
  } catch (err) {
    if (err?.name !== 'NavigationDuplicated') {
      errorMessage.value =
        'Connexion réussie, mais la redirection a échoué. Actualisez la page ou cliquez sur Accueil.';
    }
  }
}
const username = ref('');
const password = ref('');
const rememberMe = ref(false);
const showPassword = ref(false);
const errorMessage = ref('');
/** Message après redirection (ex. session fermée pour inactivité) — sans alert() */
const sessionNotice = ref('');
const loading = ref(false);

const showEmailVerificationModal = ref(false);
const emailForVerification = ref('');
const verificationCode = ref('');
const verificationError = ref('');
const verificationLoading = ref(false);
const resendLoading = ref(false);
const resendSuccessMessage = ref('');
const LOGIN_RETRY_DELAYS_MS = [800, 1800];
const mfaStep = ref(false);
const mfaToken = ref('');
const mfaCode = ref('');
const mfaLoading = ref(false);

function getDetailedErrorMessage(error) {
  const validationMessages = formatValidationErrors(error?.response?.data);
  return getErrorMessage(error, { validationMessages });
}
async function warmUpBackend() {
  try {
    await axios.get('/api/db-health/', { timeout: 10000 });
  } catch (_) {}
}

function applySessionNoticeFromQuery() {
  const raw = route.query.reason;
  const reason = Array.isArray(raw) ? raw[0] : raw;
  if (reason === 'inactivity') {
    sessionNotice.value =
      'Votre session a été fermée suite à une inactivité. Veuillez vous reconnecter.';
    const q = { ...route.query };
    delete q.reason;
    router.replace({ path: route.path, query: q }).catch(() => {});
  }
}

function isTransientLoginError(error) {
  const status = error?.response?.status;
  const code = error?.code;
  return status === 503 || code === 'ECONNABORTED' || code === 'ERR_NETWORK';
}

async function loginWithRetry(payload) {
  let lastError = null;
  const attempts = LOGIN_RETRY_DELAYS_MS.length + 1;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await axios.post('/api/login/', payload, { timeout: 90000, withCredentials: true });
    } catch (error) {
      lastError = error;
      if (!isTransientLoginError(error) || attempt === attempts - 1) {
        throw error;
      }
      const waitMs = LOGIN_RETRY_DELAYS_MS[attempt] || 1000;
      await new Promise((resolve) => setTimeout(resolve, waitMs));
    }
  }
  throw lastError;
}

function cancelMfaStep() {
  mfaStep.value = false;
  mfaToken.value = '';
  mfaCode.value = '';
}

async function submitMfaCode() {
  if (!mfaToken.value || !mfaCode.value.trim()) {
    errorMessage.value = 'Code de vérification requis.';
    return;
  }
  const guarded = await mfaSubmitGuard.run(async () => {
    mfaLoading.value = true;
    errorMessage.value = '';
    try {
      const response = await axios.post(
        '/api/login/verify-mfa/',
        { mfa_token: mfaToken.value, code: mfaCode.value.trim() },
        { timeout: 60000, withCredentials: true }
      );
      storeUserProfileFromLogin(response.data);
      await navigateAfterLogin();
    } catch (error) {
      errorMessage.value =
        formatThrottleMessage(error) || getDetailedErrorMessage(error).message;
    } finally {
      mfaLoading.value = false;
    }
  });
  if (!guarded.ok) {
    errorMessage.value = 'Veuillez patienter avant une nouvelle tentative.';
  }
}

async function handleSubmit() {
  const guarded = await loginSubmitGuard.run(async () => {
    errorMessage.value = '';
    loading.value = true;
    try {
      const response = await loginWithRetry({
        username: username.value,
        password: password.value,
      });
      const userData = response.data;
      if (userData.mfa_required && userData.mfa_token) {
        mfaToken.value = userData.mfa_token;
        mfaStep.value = true;
        mfaCode.value = '';
        return;
      }
      storeUserProfileFromLogin(userData);
      if (rememberMe.value && username.value.trim()) {
        try {
          localStorage.setItem(REMEMBER_USERNAME_KEY, username.value.trim());
        } catch (_) { /* quota / mode privé */ }
      } else {
        try {
          localStorage.removeItem(REMEMBER_USERNAME_KEY);
        } catch (_) {}
      }
      await navigateAfterLogin();
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data || {};
      if (status === 403 && data.code === 'email_not_verified') {
        emailForVerification.value = data.email || '';
        if (!emailForVerification.value && username.value) emailForVerification.value = '';
        showEmailVerificationModal.value = true;
        errorMessage.value = '';
      } else if (status === 401) {
        errorMessage.value = data.detail || "Nom d'utilisateur ou mot de passe incorrect.";
      } else if (status === 404) {
        errorMessage.value = "Utilisateur non trouvé. Vérifiez votre nom d'utilisateur.";
      } else if (status === 429) {
        errorMessage.value =
          formatThrottleMessage(error) || 'Trop de tentatives. Patientez avant de réessayer.';
      } else if (isTransientLoginError(error)) {
        errorMessage.value =
          'Connexion temporairement indisponible (serveur lent). Veuillez réessayer dans quelques secondes.';
      } else {
        errorMessage.value = getDetailedErrorMessage(error).message;
      }
    } finally {
      loading.value = false;
    }
  });
  if (!guarded.ok) {
    errorMessage.value = 'Veuillez patienter avant une nouvelle tentative.';
  }
}

function closeEmailVerificationModal() {
  showEmailVerificationModal.value = false;
}

async function submitEmailVerification() {
  if (!emailForVerification.value.trim()) {
    verificationError.value = "Veuillez saisir votre email.";
    return;
  }
  if (!verificationCode.value.trim()) {
    verificationError.value = "Veuillez saisir le code de vérification.";
    return;
  }
  const guarded = await loginVerifySubmitGuard.run(async () => {
    verificationLoading.value = true;
    verificationError.value = '';
    try {
      await axios.post(
        '/api/email-verification/verify/',
        {
          email: emailForVerification.value.trim(),
          code: verificationCode.value.trim(),
        },
        { timeout: 30000 }
      );
      showEmailVerificationModal.value = false;
      errorMessage.value = 'Email vérifié avec succès. Veuillez vous reconnecter.';
    } catch (error) {
      verificationError.value =
        formatThrottleMessage(error) || getDetailedErrorMessage(error).message;
    } finally {
      verificationLoading.value = false;
    }
  });
  if (!guarded.ok) {
    verificationError.value = 'Veuillez patienter avant une nouvelle tentative.';
  }
}

async function resendVerificationCode() {
  if (!emailForVerification.value.trim()) {
    verificationError.value = "Veuillez saisir votre email pour renvoyer le code.";
    return;
  }
  const guarded = await loginResendSubmitGuard.run(async () => {
    resendLoading.value = true;
    verificationError.value = '';
    resendSuccessMessage.value = '';
    try {
      await axios.post(
        '/api/email-verification/resend/',
        { email: emailForVerification.value.trim() },
        { timeout: 30000 }
      );
      resendSuccessMessage.value = 'Un nouveau code a été envoyé à votre adresse email.';
    } catch (error) {
      verificationError.value =
        formatThrottleMessage(error) || getDetailedErrorMessage(error).message;
    } finally {
      resendLoading.value = false;
    }
  });
  if (!guarded.ok) {
    verificationError.value = 'Un code a déjà été demandé récemment. Patientez avant de renvoyer.';
  }
}

onMounted(async () => {
  try {
    const savedUsername = localStorage.getItem(REMEMBER_USERNAME_KEY);
    if (savedUsername) {
      username.value = savedUsername;
      rememberMe.value = true;
    }
  } catch (_) {}
  warmUpBackend();
  applySessionNoticeFromQuery();
  if (isAuthenticatedSession()) {
    const valid = await verifyServerSession(axios);
    if (valid) {
      await navigateAfterLogin();
    } else {
      clearAuthenticatedSession();
    }
  }
});
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--surface, #f7f9fc);
  font-family: var(--font-body);
}

.auth-page--login .auth-page__wrap {
  width: 100%;
  max-width: 440px;
  flex: 1;
  justify-content: flex-start;
  padding-top: 0;
  margin-top: -1.5rem;
}

.auth-page__wrap {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 1rem 2rem;
}

.auth-card {
  background: #fff;
  border-radius: 1.25rem;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--line);
  padding: 2rem 1.75rem;
  width: 100%;
}

.auth-card--login {
  border-radius: 16px;
  box-shadow: var(--shadow-lg);
  border-top: 3px solid var(--brand-gold);
}
.card-enter-active {
  transition: opacity 0.4s ease, transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.card-enter-from {
  opacity: 0;
  transform: translateY(16px);
}

.auth-card__header {
  text-align: center;
  padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid rgba(212, 160, 23, 0.2);
}

.auth-card__header--login {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 2.25rem 1.75rem 2.75rem;
  background:
    linear-gradient(180deg, rgba(84, 57, 41, 0.08), transparent 40%),
    var(--brand-brown);
  border-bottom: none;
  border-radius: 0;
  box-sizing: border-box;
}


.auth-card__title {
  font-family: var(--font-display);
  font-size: clamp(1.75rem, 1.35rem + 1.5vw, 2.5rem);
  font-weight: 600;
  color: var(--brand-gold);
  margin: 0 0 0.5rem 0;
  text-align: center;
  letter-spacing: -0.02em;
  line-height: 1.15;
}

.auth-card__title--login {
  color: #f7f1e8 !important;
  -webkit-text-fill-color: #f7f1e8;
  margin-bottom: 0;
}

.auth-form__group {
  margin-bottom: 1.25rem;
}
.auth-form__label {
  display: block;
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--brand-navy);
  margin-bottom: 0.5rem;
}
.auth-form__input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.auth-form__icon {
  position: absolute;
  left: 1rem;
  width: 1.25rem;
  height: 1.25rem;
  color: #94a3b8;
  pointer-events: none;
}
.auth-form__input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #0f172a;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.auth-form__input::placeholder {
  color: #94a3b8;
}
.auth-form__input:focus {
  outline: none;
  border-color: var(--brand-gold);
  box-shadow: 0 0 0 4px var(--brand-gold-soft);
}
.auth-form__input--with-btn {
  padding-right: 3rem;
}
.auth-form__toggle-pwd {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  /* Taille fixe + appearance: none : sans ça, le style natif du <button> sur
     mobile (padding/état "pressed" du navigateur) peut faire bouger l'icône
     au clic/survol au lieu de la garder immobile dans son cadre. */
  width: 2rem;
  height: 2rem;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  appearance: none;
  -webkit-appearance: none;
  color: #64748b;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 0.5rem;
}
.auth-form__toggle-pwd:hover {
  color: #0f172a;
  background: #f1f5f9;
}

.auth-form__options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}
.auth-form__remember {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #475569;
  cursor: pointer;
}
.auth-form__checkbox {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.auth-form__checkmark {
  width: 1.125rem;
  height: 1.125rem;
  border: 2px solid #cbd5e1;
  border-radius: 0.375rem;
  flex-shrink: 0;
  transition: border-color 0.2s, background 0.2s;
}
.auth-form__checkbox:checked + .auth-form__checkmark {
  background: var(--brand-gold);
  border-color: var(--brand-gold);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%230f172a'%3E%3Cpath fill-rule='evenodd' d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z' clip-rule='evenodd'/%3E%3C/svg%3E");
}
.auth-form__link {
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--brand-gold-dark);
  text-decoration: none;
  transition: color 0.2s;
}
.auth-form__link:hover {
  color: var(--brand-navy);
}

.auth-form__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.75rem;
}

.auth-form__notice {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  line-height: 1.45;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 0.75rem;
}
.auth-form__notice-icon {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  color: #d97706;
  margin-top: 0.1rem;
}
.auth-form__submit {
  width: 100%;
  padding: 0 var(--space-5);
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--brand-brown);
  background: var(--brand-gold);
  border: 1px solid transparent;
  border-radius: var(--radius-pill);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease), transform var(--dur-fast) var(--ease), box-shadow var(--dur-fast) var(--ease);
  position: relative;
  min-height: 48px;
}
.auth-form__submit:hover:not(:disabled) {
  background: var(--brand-gold-light);
  color: var(--brand-brown);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}
.auth-form__submit:active:not(:disabled) {
  transform: none;
}
.auth-form__submit:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.auth-form__spinner {
  display: inline-block;
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid color-mix(in srgb, var(--brand-ink) 25%, transparent);
  border-top-color: var(--brand-ink);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.auth-form__footer {
  margin: 1.5rem 0 0;
  font-size: 0.875rem;
  color: #64748b;
  text-align: center;
}
.auth-form__footer .auth-form__link {
  margin-left: 0.25rem;
}

@media (max-width: 480px) {
  .auth-card {
    padding: 1.5rem 1.25rem;
  }
  .auth-form__submit {
    min-height: 52px;
    font-size: 1rem;
  }
}
</style>
